import "./PageShortsQueue.css";
import { Instant } from "@js-joda/core";
import { type FC, useMemo } from "react";
import { Table } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import {
	type ContentFileDto,
	canCancelScheduledPost,
	canEditScheduledPost,
	Platform,
	type PostDto,
	PostStatus,
	postLabel,
	postQueueStatusDetail,
	postQueueStatusLabel,
	postQueueYouTubeUrl,
	SCHEDULE_EDIT_LOCK_MINUTES,
} from "shared";
import {
	usePostsActions,
	useSuspensePosts,
	useSuspensePublishingQueue,
} from "../calendar/usePosts";
import { useModal } from "../common/modal/Modal";
import { SimpleTooltip } from "../common/SimpleTooltip";
import { TimeFormatter, useTime } from "../common/time/useTime";
import { Button } from "../components/Button";
import { Card } from "../components/Card";
import { useSuspenseMedia } from "../content/useMediaBatch";
import { useFeatureFlag } from "../hooks/use-app-metadata";
import { LinkGen } from "../LinkGen";
import { AppPage } from "../layout/AppLayout";

const STATUS_BADGE: Record<string, string> = {
	Queued: "bg-secondary",
	Publishing: "bg-info text-dark",
	Published: "bg-success",
	Failed: "bg-danger",
	Retrying: "bg-warning text-dark",
	Paused: "bg-warning text-dark",
};

const thumbnailUrlForPost = (
	post: PostDto,
	mediaById: Map<number, ContentFileDto>,
): string | undefined => {
	if (post.youtubeThumbnailFileId) {
		const thumb = mediaById.get(post.youtubeThumbnailFileId);
		if (thumb) {
			return thumb.thumbnails["256"] ?? thumb.url;
		}
	}
	const videoId = post.files?.[0];
	if (videoId) {
		const video = mediaById.get(videoId);
		if (video) {
			return video.thumbnails["256"] ?? video.thumbnails.full ?? video.url;
		}
	}
	return undefined;
};

const canShowEdit = (post: PostDto): boolean => {
	if (post.status === PostStatus.FAILED) {
		return true;
	}
	if (post.status === PostStatus.SCHEDULED) {
		return canEditScheduledPost(post);
	}
	return false;
};

const ShortsQueueTable: FC = () => {
	const { IDs } = useSuspensePublishingQueue(Platform.YOUTUBE);
	const posts = useSuspensePosts(IDs);
	const mediaIds = useMemo(() => {
		const ids = new Set<number>();
		for (const post of posts) {
			if (post.youtubeThumbnailFileId) {
				ids.add(post.youtubeThumbnailFileId);
			}
			for (const fileId of post.files ?? []) {
				ids.add(fileId);
			}
		}
		return Array.from(ids);
	}, [posts]);
	const media = useSuspenseMedia(mediaIds);
	const mediaById = useMemo(
		() => new Map(media.map((file) => [file.id, file])),
		[media],
	);
	const { format } = useTime();
	const { confirm } = useModal();
	const { remove } = usePostsActions();

	const handleCancel = (post: PostDto) => {
		confirm({
			children: (
				<>
					Cancel scheduled Short <strong>{postLabel(post)}</strong>? This
					removes it from the queue.
				</>
			),
			confirmText: "Cancel Short",
			variant: "danger",
			onConfirm: async () => {
				await remove.mutateAsync(post.id);
			},
		});
	};

	if (posts.length === 0) {
		return (
			<Card>
				<Card.Body>
					<p className="shorts-queue-empty mb-0">
						No Shorts in the queue yet.{" "}
						<Link to={LinkGen.ShortsCreate()}>Create a Short</Link> to get
						started.
					</p>
				</Card.Body>
			</Card>
		);
	}

	return (
		<Card>
			<Card.Body style={{ padding: 0, overflowX: "auto" }}>
				<Table className="shorts-queue-table mb-0">
					<thead>
						<tr>
							<th scope="col">Thumbnail</th>
							<th scope="col">Title</th>
							<th scope="col">Channel</th>
							<th scope="col">Scheduled</th>
							<th scope="col">Status</th>
							<th scope="col">Actions</th>
						</tr>
					</thead>
					<tbody>
						{posts.map((post) => {
							const status = postQueueStatusLabel(post);
							const detail = postQueueStatusDetail(post);
							const youtubeUrl = postQueueYouTubeUrl(post);
							const thumbUrl = thumbnailUrlForPost(post, mediaById);
							const scheduledInstant = post.scheduledAt
								? Instant.parse(post.scheduledAt)
								: post.publishedAt
									? Instant.parse(post.publishedAt)
									: Instant.parse(post.updatedAt);
							const showEdit = canShowEdit(post);
							const editLocked =
								post.status === PostStatus.SCHEDULED &&
								!canEditScheduledPost(post);
							const showCancel = canCancelScheduledPost(post);

							return (
								<tr key={post.id}>
									<td>
										{thumbUrl ? (
											<img
												className="shorts-queue-thumb"
												src={thumbUrl}
												alt=""
											/>
										) : (
											<div
												className="shorts-queue-thumb-placeholder"
												aria-hidden
											/>
										)}
									</td>
									<td>
										<span className="shorts-queue-title d-inline-block text-truncate">
											{postLabel(post)}
										</span>
									</td>
									<td>{post.youtubeChannelName ?? "—"}</td>
									<td>{format(TimeFormatter.DateTime, scheduledInstant)}</td>
									<td>
										{detail ? (
											<SimpleTooltip tooltip={detail} placement="top">
												<span
													className={`badge ${STATUS_BADGE[status] ?? "bg-secondary"}`}
												>
													{status}
												</span>
											</SimpleTooltip>
										) : (
											<span
												className={`badge ${STATUS_BADGE[status] ?? "bg-secondary"}`}
											>
												{status}
											</span>
										)}
									</td>
									<td>
										<div className="shorts-queue-actions">
											{showEdit && <Link to={LinkGen.Post(post.id)}>Edit</Link>}
											{editLocked && (
												<SimpleTooltip
													tooltip={`Edits are locked within ${SCHEDULE_EDIT_LOCK_MINUTES} minutes of the scheduled publish time.`}
													placement="top"
												>
													<span className="text-muted">Edit locked</span>
												</SimpleTooltip>
											)}
											{showCancel && (
												<Button
													variant="link"
													size="xsm"
													onClick={() => handleCancel(post)}
												>
													Cancel
												</Button>
											)}
											{youtubeUrl && (
												<a
													href={youtubeUrl}
													target="_blank"
													rel="noopener noreferrer"
												>
													View on YouTube
												</a>
											)}
										</div>
									</td>
								</tr>
							);
						})}
					</tbody>
				</Table>
			</Card.Body>
		</Card>
	);
};

export const PageShortsQueue = () => {
	const navigate = useNavigate();
	const isYouTubeEnabled = useFeatureFlag("YouTubeIntegration");

	return (
		<AppPage
			title="YouTube Shorts queue"
			subtitle="Queued, publishing, and recently published Shorts."
			headerActions={
				isYouTubeEnabled ? (
					<Button
						icon="add_circle"
						onClick={() => navigate(LinkGen.ShortsCreate())}
					>
						New Short
					</Button>
				) : undefined
			}
		>
			{isYouTubeEnabled ? (
				<ShortsQueueTable />
			) : (
				<Card>
					<Card.Body>
						<p className="mb-0 text-muted">
							YouTube Shorts are not enabled for this workspace.
						</p>
					</Card.Body>
				</Card>
			)}
		</AppPage>
	);
};
